/**
 * 파일 첨부 — 올리고, 본문에 끼울 **마크다운 조각**을 받는다.
 *
 * **댓글 팀과의 접점이다**(2026-09-01 합의). `uploadAndInsert` 의 이름과
 * 모양(File → Promise<string>)을 바꾸면 그쪽 붙여넣기 핸들러가 깨진다.
 *
 * 전송은 multipart 가 아니라 **바이트 몸통 그대로**다 — 서버가
 * `python-multipart` 를 안 들이기로 해서다(`app/routers/attachments.py`
 * 머리말). 이름은 쿼리로 실어 보낸다.
 *
 * QA 가 스크린샷을 텔레그램에 올리고 티켓 비고에 「거기 올렸다」고 적던
 * 자리를 닫는 기능이다 — 버그의 절반이 밖에 남으면 「히스토리를 한 곳에」
 * 가 반쪽이 된다.
 */

import { ApiError, NotLoggedInError } from './api';

export type Attachment = {
  id: string;
  /** 브라우저가 부를 주소(`/api/attachments/…`). `<img src>` 에 그대로 쓴다. */
  url: string;
  /** 서버가 다듬은 표시용 이름. 경로가 아니다. */
  filename: string;
  /** 서버가 **바이트를 보고** 판정한 형식 — 보낸 쪽 말이 아니다. */
  mime: string;
  size: number;
  /** 본문에 그대로 끼울 조각. **모양의 주인은 서버다.** */
  markdown: string;
};

/**
 * 서버 오류 본문에서 사람이 읽을 문구를 꺼낸다. `api.ts` 의 `readError`
 * 와 같은 규칙인데 그 함수가 밖으로 안 나와 있고, `api.ts` 는 이 작업의
 * 소유 밖이라 최소본을 여기 둔다.
 */
async function readErrorText(res: Response): Promise<string> {
  const text = await res.text();
  if (text) {
    try {
      const data = JSON.parse(text) as { detail?: unknown };
      if (typeof data.detail === 'string') return data.detail;
    } catch {
      return text;
    }
  }
  return `요청이 실패했다 (HTTP ${res.status})`;
}

/** 파일 하나를 올린다. 조각만 필요하면 아래 `uploadAndInsert` 를 써라. */
export async function uploadAttachment(file: File): Promise<Attachment> {
  // 붙여넣은 스크린샷은 이름이 빌 수 있다 — 서버도 기본 이름을 주지만
  // 여기서 채워 보내면 응답의 표시 이름이 처음부터 사람 것이 된다.
  const name = file.name.trim() ? file.name : 'screenshot.png';
  let res: Response;
  try {
    res = await fetch(`/api/attachments?filename=${encodeURIComponent(name)}`, {
      method: 'POST',
      body: file,
      // 서버는 이 값을 안 믿고 바이트를 직접 판정한다. 그래도 실어 보낸다 —
      // 프록시·로그가 요청을 읽을 수 있어야 한다.
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
  } catch (err) {
    throw new Error(
      `서버에 닿지 못했다: ${err instanceof Error ? err.message : String(err)}`,
      { cause: err },
    );
  }
  if (res.status === 401) throw new NotLoggedInError();
  if (!res.ok) throw new ApiError(res.status, await readErrorText(res));
  return (await res.json()) as Attachment;
}

/**
 * 파일을 올리고 본문에 끼울 **마크다운 조각**을 돌려준다.
 * 예: `![결제 오류 화면.png](/api/attachments/6f2c…)`
 */
export async function uploadAndInsert(file: File): Promise<string> {
  const uploaded = await uploadAttachment(file);
  return uploaded.markdown;
}

/**
 * 붙여넣기·끌어놓기 자료에서 **그림 파일만** 골라낸다.
 *
 * 글자만 붙여넣은 경우 빈 배열이다 — 부르는 쪽은 그때 브라우저 기본
 * 동작(글자 삽입)을 막지 말아야 한다.
 */
export function imageFilesFromDataTransfer(data: DataTransfer | null): File[] {
  if (!data) return [];
  const files = Array.from(data.files ?? []).filter(isAttachable);
  if (files.length > 0) return files;
  // 일부 브라우저는 붙여넣기를 `files` 에 안 싣고 `items` 로만 준다.
  const fromItems: File[] = [];
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind === 'file' && isAttachableType(item.type)) {
      const file = item.getAsFile();
      if (file) fromItems.push(file);
    }
  }
  return fromItems;
}

/**
 * 서버가 받아 주는 형식인가 — **거르는 것은 조심이지 방어가 아니다.**
 * 판정의 주인은 서버(`app/attachment_store.py` 의 매직 바이트)이고,
 * 여기서 미리 거르는 것은 「올렸다가 400 을 받는」 왕복을 없애기 위해서다.
 *
 * ⚠ 2026-09-02 에 영상·PDF 가 늘었다. 서버 목록이 바뀌면 여기도 바뀐다 —
 * 어긋나면 **서버는 받는데 화면이 안 보내는** 조용한 상태가 된다.
 */
function isAttachableType(type: string): boolean {
  return (
    type.startsWith('image/') ||
    type === 'video/mp4' ||
    type === 'video/webm' ||
    type === 'application/pdf'
  );
}

function isAttachable(file: File): boolean {
  return isAttachableType(file.type);
}
